const normalize = (value) => String(value || "").trim().toLowerCase();

const legacyConfigKey = (link) => JSON.stringify([
  normalize(link.campaign_name || link.name),
  normalize(link.destination),
  link.campaign_type || "direct",
  link.pass_email !== false,
  link.pass_email_format || "base64",
  Boolean(link.track_b2b),
  Boolean(link.uses_default_destination),
  link.landing_page || "blank",
]);

const aggregateSyncStatus = (variants) => {
  if (variants.some((link) => (link.sync_drift_areas || []).length > 0)) return "drift";
  const statuses = variants.map((link) => link.sync_status || (link.last_synced_at ? "synced" : "pending"));
  if (statuses.includes("failed")) return "failed";
  if (statuses.includes("syncing")) return "syncing";
  if (statuses.includes("pending")) return "pending";
  return "synced";
};

export const groupLinksIntoCampaigns = (links) => {
  const groups = new Map();

  links.forEach((link) => {
    const key = link.campaign_id ? `campaign:${link.campaign_id}` : `legacy:${legacyConfigKey(link)}`;
    if (!groups.has(key)) {
      groups.set(key, {
        id: link.campaign_id || `legacy-${link.id}`,
        rawName: String(link.campaign_name || link.name || "").trim(),
        variants: [],
      });
    }
    groups.get(key).variants.push(link);
  });

  const chronological = Array.from(groups.values()).sort((left, right) => {
    const leftTime = Math.min(...left.variants.map((item) => Date.parse(item.created_at || 0) || 0));
    const rightTime = Math.min(...right.variants.map((item) => Date.parse(item.created_at || 0) || 0));
    return leftTime - rightTime;
  });

  chronological.forEach((campaign, index) => {
    campaign.name = campaign.rawName || `Campaign ${index + 1}`;
    campaign.variants.sort((left, right) => (
      String(left.domain).localeCompare(String(right.domain))
      || String(left.path).localeCompare(String(right.path))
    ));
    campaign.clicks = campaign.variants.reduce((total, link) => total + Number(link.clicks || 0), 0);
    campaign.variantCount = campaign.variants.length;
    campaign.domains = Array.from(new Set(campaign.variants.map((link) => link.domain))).sort();
    campaign.domainCount = campaign.domains.length;
    campaign.activeCount = campaign.variants.filter((link) => link.is_active).length;
    campaign.isActive = campaign.activeCount > 0;
    campaign.activityStatus = campaign.activeCount === campaign.variantCount
      ? "Active"
      : campaign.activeCount === 0 ? "Inactive" : "Mixed";
    campaign.syncStatus = aggregateSyncStatus(campaign.variants);
    campaign.createdAt = campaign.variants.reduce((latest, link) => (
      String(link.created_at || "") > latest ? String(link.created_at || "") : latest
    ), "");
  });

  return chronological.sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt)));
};

export const filterCampaigns = (campaigns, searchTerm) => {
  const query = normalize(searchTerm);
  if (!query) return campaigns;
  return campaigns.filter((campaign) => (
    normalize(campaign.name).includes(query)
    || campaign.variants.some((link) => [link.domain, link.path, link.destination]
      .some((value) => normalize(value).includes(query)))
  ));
};